import {useQuery} from "@tanstack/react-query";
import {api} from "@/data/api";
import {PostalCodeLookupRequest, PostalCodeLookupResponse} from "@/data/entities/postalCode";
import {POSTAL_CODE_DIGITS_PATTERN, POSTAL_CODE_LETTERS_PATTERN} from "@/data/entities/deliveryAddress";

/**
 * Lookup request for a complete, valid postal code ("6811" + "AA"), or
 * `null` while it's still being typed.
 */
const toLookupRequest = (
    digits: string | null | undefined,
    letters: string | null | undefined,
): PostalCodeLookupRequest | null => {
    const postalCodeDigits = (digits ?? "").trim();
    const postalCodeLetters = (letters ?? "").trim().toUpperCase();

    return POSTAL_CODE_DIGITS_PATTERN.test(postalCodeDigits) && POSTAL_CODE_LETTERS_PATTERN.test(postalCodeLetters)
        ? {postalCodeDigits, postalCodeLetters}
        : null;
};

/**
 * Looks up the street and city of a postal code, once both parts are filled
 * in. Results are cached per postal code: they hardly ever change, and the
 * API allows 60 requests per minute.
 */
export const usePostalCodeLookup = (digits: string | null | undefined, letters: string | null | undefined) => {
    const request: PostalCodeLookupRequest | null = toLookupRequest(digits, letters);

    return useQuery<PostalCodeLookupResponse>({
        queryKey: ["postal-code-lookup", request?.postalCodeDigits, request?.postalCodeLetters],
        queryFn: () => api.postalCode.getPostalCodeData(request!),
        enabled: request !== null,
        staleTime: Infinity,
        gcTime: 60 * 60 * 1000,
        retry: false,
    });
};